// @noflow

// main imports
import React, { Component } from "react";
import { History, Location } from "history";
import { match } from "react-router-dom";
import { FormattedMessage } from "react-intl";

// models
import { LINE_TAGS, LINE_TYPES } from "@/lib/ManufacturingTags.generated";
import * as BusinessLogic from "@/lib/BusinessLogic";

// subcomponents
import ErrorComponent from "@/components/ErrorComponent";
import Toggle from "react-toggle";
import { translate } from "@/components/IntlProviderWrapper";
import LinesAddCategoryItemModal from "./LinesAddCategoryItemModal";

// backend
import { getBackend } from "@/lib/backend/Backend2";
import * as t from "@/lib/backend/manufacturing2.generated.types";
import { stringCompare, classNames } from "@/lib/Util";
import LineEditTags, { isSpecialTag } from "./LineEditTags";
import { onChangeUpdateTags, type TagUpdateValue } from "@/components/react/ReactTableTags";
import Loader from "@/components/Loader";

import img_banner from "@/img/banner.png";

const DEFAULT_LINE_FACTOR = 1;

// type definitions
type ITagDisplay = {
    name: string;
    id: string;
};

type ICategoryItemExt = t.ICategoryItem & {
    deleted?: boolean;
};

type ICategoryExt = t.ICategory & {
    items: ICategoryItemExt[];
};

type Props = {
    match: match<{ uuid?: string }>;
    history: History;
    location: Location;
};

export type State = {
    bottleneck: boolean;
    external_id: string;
    uuid: string;
    title: string;
    plant_uuid: string;
    skip_planning: boolean;
    skip_shift_board: boolean;
    skip_stock_analysis: boolean;
    tags: any;
    type: string;
    plants: t.IPlantData[];
    weight: number;
    factor: string;
    message: string;
    componentTitle: string;
    error: string;
    warning: string;
    loading: boolean;
    parent_id: number | null;
    categories: ICategoryExt[];
    original_category_item_ids: Set<number>;
    show_create_edit_category_modal: boolean;
    line_id: number | null;
    loadingMessage: string;
};

/**
 * Form to create new line.
 */
export default class LineCreate extends Component<Props, State> {
    SUBMIT_MESSAGE = "common.add_line_loading";

    constructor(props: Props) {
        super(props);
        const state: State = {
            bottleneck: false,
            external_id: "",
            uuid: "",
            title: "",
            plant_uuid: "",
            skip_planning: false,
            skip_shift_board: false,
            skip_stock_analysis: false,
            tags: {},
            type: LINE_TYPES.ordinary,
            plants: [],
            weight: 10.0,
            factor: String(DEFAULT_LINE_FACTOR),
            message: "",
            componentTitle: translate("Manufacturing.Lines.add_new_line", "Add New Line"),
            error: "",
            warning: "",
            loading: false,
            parent_id: null,
            categories: [],
            original_category_item_ids: new Set(),
            show_create_edit_category_modal: false,
            line_id: null,
            loadingMessage: "common.loading",
        };
        this.state = state;
    }

    /**
     * Loading component data (plants).
     */
    async componentDidMount() {
        try {
            // state from previous page (Line Details) (allows copy line functionality)
            const state: Partial<State> = this.props.location.state || {};
            const { plants }: { plants: t.IPlantData[] } = await getBackend().manufacturing.getPlants({});
            this.setState({
                ...(state as State),
                plants,
            });
        } catch (e: any) {
            this.setState({ error: e });
        }
    }

    /**
     * Basic form validation.
     */
    validateShallow() {
        const factor_num = this.getFactorForSubmit();
        return (
            this.state.title.length > 0 &&
            this.state.external_id.length > 0 &&
            this.state.plant_uuid.length > 0 &&
            factor_num >= 0
        );
    }

    getFactorForSubmit(): number {
        const factor = parseFloat(this.state.factor);
        return Number.isFinite(factor) ? factor : DEFAULT_LINE_FACTOR;
    }

    /**
     * Handling change of any input component.
     */
    handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const target = event.currentTarget;
        const name = target.name;
        const value: string | boolean =
            target.type === "checkbox" ? (target as HTMLInputElement).checked : target.value;

        const is_tag = Object.keys(LINE_TAGS).includes(name) && !isSpecialTag(name);
        if (is_tag) {
            const tags = this.state.tags || {};
            tags[name] = value.toString();
            this.setState(prev_state => {
                return {
                    ...prev_state,
                    [name]: value,
                    tags,
                };
            });
        } else {
            this.setState(prev_state => ({
                ...prev_state,
                [name]: value,
            }));
        }
    };

    /**
     * Handling submission of the form. Basic checks and saves with error handling.
     */
    handleSubmit = async () => {
        this.setState({ loading: true, loadingMessage: this.SUBMIT_MESSAGE });
        try {
            if (this.validateShallow()) {
                const tags = this.state.tags;
                // SPECIAL CASE - line type is saved as a tag
                tags[LINE_TAGS.type] = this.state.type;
                let back_line_uuid = this.state.uuid;
                let line_id = this.state.line_id;

                if (this.state.uuid === "") {
                    const { uuid } = await getBackend().manufacturing.insertLine({
                        external_id: this.state.external_id,
                        factor: this.getFactorForSubmit(),
                        plant_uuid: this.state.plant_uuid,
                        tags,
                        title: this.state.title,
                        weight: this.state.weight,
                    });
                    back_line_uuid = uuid;

                    // Fetch the line to get the id
                    const line_result = await getBackend().manufacturing.getLine({
                        line_uuid: back_line_uuid,
                    });
                    line_id = line_result.line.id;
                } else {
                    // update current object
                    await getBackend().manufacturing.updateLine({
                        external_id: this.state.external_id,
                        factor: this.getFactorForSubmit(),
                        line_uuid: this.state.uuid,
                        tags,
                        title: this.state.title,
                        weight: this.state.weight,
                    });
                }

                // Save categories after line is created/updated
                if (line_id !== null && this.state.categories.length > 0) {
                    for (const category of this.state.categories) {
                        // Remove items marked as deleted that were originally loaded
                        const deleted_items = category.items.filter((item: ICategoryItemExt) => {
                            return (
                                item.deleted && item.id !== null && this.state.original_category_item_ids.has(item.id)
                            );
                        });
                        for (const category_item of deleted_items) {
                            const category_item_id = category_item.id;
                            if (category_item_id !== null) {
                                await getBackend().manufacturing.removeCategoryItemFromLine({
                                    line_id: line_id,
                                    category_id: category.id,
                                    category_item_id: category_item_id,
                                });
                            }
                        }

                        // Add only new items (not originally loaded, not deleted)
                        const items_to_add = category.items.filter((item: ICategoryItemExt) => {
                            return (
                                !item.deleted && item.id !== null && !this.state.original_category_item_ids.has(item.id)
                            );
                        });
                        for (const category_item of items_to_add) {
                            const category_item_id = category_item.id;
                            if (category_item_id !== null) {
                                await getBackend().manufacturing.addCategoryItemToLine({
                                    line_id: line_id,
                                    category_id: category.id,
                                    category_item_id: category_item_id,
                                });
                            }
                        }
                    }
                }

                await BusinessLogic.forceManufacturingCacheRefresh();
                // If parent id is set, we can't navigate to child
                if (this.state.parent_id) {
                    this.props.history.push(`/digital-twin/resources/lines/`);
                } else {
                    this.props.history.push(
                        `/digital-twin/resources/lines/${back_line_uuid}?plant_uuid=${this.state.plant_uuid}`
                    );
                }
            } else {
                this.setState({
                    warning: translate(
                        "Manufacturing.Lines.cannot_submit_missing_values",
                        "Cannot submit a form with missing values."
                    ),
                });
                window.scrollTo(0, 0);
            }
        } catch (err: unknown) {
            this.setState({ error: (err as Error).message });
        }
        this.setState({ loading: false });
    };

    /**
     * Renders tag.
     * @param {ITagDisplay} tag
     */
    renderTagField(tag: ITagDisplay) {
        return (
            <div className="form-group" key={tag.id}>
                <label>{tag.name}</label>
                <br />
                <Toggle name={tag.id} checked={this.state[tag.id]} onChange={this.handleInputChange} />
            </div>
        );
    }

    /**
     * Renders tag.
     * @param {ITagDisplay} tag
     */
    renderTypeField() {
        const items = Object.keys(LINE_TYPES)
            .map((type_i, i) => {
                return {
                    key: i,
                    value: type_i,
                    title: translate("Manufacturing.Lines.Types." + type_i, type_i),
                };
            })
            .sort((a, b) => stringCompare(a.title, b.title));
        return (
            <div className="form-group">
                <label>
                    <FormattedMessage id="common.type" defaultMessage="Type" />:
                </label>
                <select
                    name="type"
                    className="form-control"
                    value={this.state.type}
                    onChange={this.handleInputChange}
                    disabled={this.state.parent_id !== null}
                >
                    {items.map(item => {
                        return (
                            <option key={item.key} value={item.value}>
                                {item.title}
                            </option>
                        );
                    })}
                </select>
            </div>
        );
    }

    onChangeCell = (name: string) => (value: TagUpdateValue) => {
        this.setState({ tags: onChangeUpdateTags(this.state.tags, name, value) });
    };

    handleRemoveCategoryItemFromLine = (category: ICategoryExt, category_item: ICategoryItemExt) => {
        if (!category || !category_item) {
            return;
        }

        // Mark item as deleted instead of removing it
        const updated_categories = [...this.state.categories];
        const updated_category = updated_categories.find(c => c.id === category.id);
        if (updated_category) {
            updated_category.items = updated_category.items.map(item =>
                item.id === category_item.id ? { ...item, deleted: true } : item
            );
        }
        this.setState({
            categories: updated_categories,
        });
    };

    handleUndoRemoveCategoryItemFromLine = (category: ICategoryExt, category_item: ICategoryItemExt) => {
        if (!category || !category_item) {
            return;
        }

        // Remove deleted flag to restore the item
        const updated_categories = [...this.state.categories];
        const updated_category = updated_categories.find(c => c.id === category.id);
        if (updated_category) {
            updated_category.items = updated_category.items.map(item =>
                item.id === category_item.id ? { ...item, deleted: false } : item
            );
        }
        this.setState({
            categories: updated_categories,
        });
    };

    handleAddCategoryItemToLine = () => {
        this.setState({ show_create_edit_category_modal: true });
    };

    handleSaveLineCategory = async (category: t.ICategory, category_item: t.ICategoryItem) => {
        const category_item_id = category_item.id;
        if (category_item_id === null) {
            this.setState({
                error: translate("common.error_save", "An error occurred while saving data"),
            });
            return;
        }

        // Update state only, don't save to backend yet
        const category_item_ext: ICategoryItemExt = {
            ...category_item,
            deleted: false,
        };

        const updated_categories = [...this.state.categories];
        const updated_category = updated_categories.find(c => c.id === category.id);
        if (updated_category) {
            // Check if item already exists
            const item_exists = updated_category.items.some(item => item.id === category_item_id);
            if (!item_exists) {
                updated_category.items = [...updated_category.items, category_item_ext];
            }
        } else {
            updated_categories.push({
                ...category,
                items: [category_item_ext],
            });
        }
        this.setState({
            categories: updated_categories,
            show_create_edit_category_modal: false,
        });
    };

    /**
     * Rendering JSX for current component.
     */
    render() {
        // Disable fields if we're editing a child line
        const disabled = this.state.parent_id !== null;
        return (
            <div key="ds_new">
                <section key="1">
                    <img src={img_banner} alt="banner" className="img-fluid-header banner_img" />
                </section>
                <section id="profile_edit" className="line-create new_line" key="2">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="white_box">
                                    {this.state.loading ? (
                                        <div className="tab-content">
                                            <FormattedMessage id={this.state.loadingMessage} />
                                            <Loader small={true} />
                                        </div>
                                    ) : (
                                        <React.Fragment>
                                            <h2 className="mb-4">{this.state.componentTitle}</h2>
                                            {this.state.parent_id && (
                                                <div className="form-group">
                                                    <label className="text-muted">
                                                        <FormattedMessage
                                                            id="common.parent_id"
                                                            defaultMessage="Parent ID"
                                                        />
                                                        :
                                                    </label>
                                                    <span className="ml-2 text-muted">{this.state.parent_id}</span>
                                                </div>
                                            )}
                                            <ErrorComponent msg={this.state.error} type="error" />
                                            <ErrorComponent msg={this.state.warning} type="warning" />
                                            <div className="tab-content">
                                                <div className="form-group">
                                                    <label>
                                                        <FormattedMessage id="common.title" defaultMessage="Title" />:
                                                    </label>
                                                    <input
                                                        name="title"
                                                        type="text"
                                                        disabled={disabled}
                                                        className="form-control"
                                                        value={this.state.title}
                                                        onChange={this.handleInputChange}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label>
                                                        <FormattedMessage
                                                            id="common.external_id"
                                                            defaultMessage="External ID"
                                                        />
                                                        :
                                                    </label>
                                                    <input
                                                        name="external_id"
                                                        type="text"
                                                        disabled={disabled}
                                                        className="form-control"
                                                        value={this.state.external_id}
                                                        onChange={this.handleInputChange}
                                                    />
                                                </div>
                                                {this.state.uuid === "" && (
                                                    <div className="form-group">
                                                        <label>
                                                            <FormattedMessage
                                                                id="common.plant"
                                                                defaultMessage="Plant"
                                                            />
                                                            :
                                                        </label>
                                                        <select
                                                            name="plant_uuid"
                                                            className="form-control"
                                                            value={this.state.plant_uuid}
                                                            onChange={this.handleInputChange}
                                                        >
                                                            <option value="">
                                                                {translate("common.select", "Select")}...
                                                            </option>
                                                            {this.state.plants.map((plant, i) => {
                                                                return (
                                                                    <option key={i} value={plant.uuid}>
                                                                        {plant.title}
                                                                    </option>
                                                                );
                                                            })}
                                                        </select>
                                                    </div>
                                                )}
                                                {this.renderTypeField()}
                                                <div className="form-group">
                                                    <label>
                                                        <FormattedMessage id="common.weight" defaultMessage="Weight" />:
                                                    </label>
                                                    <input
                                                        name="weight"
                                                        type="text"
                                                        disabled={disabled}
                                                        className="form-control"
                                                        value={this.state.weight}
                                                        onChange={this.handleInputChange}
                                                    />
                                                </div>
                                                <div className="form-group">
                                                    <label htmlFor="line-factor">
                                                        {translate(
                                                            "Manufacturing.Lines.competence_factor",
                                                            "Competence factor"
                                                        )}
                                                        :
                                                    </label>
                                                    <input
                                                        id="line-factor"
                                                        name="factor"
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        disabled={disabled}
                                                        className="form-control"
                                                        value={this.state.factor}
                                                        onChange={this.handleInputChange}
                                                    />
                                                </div>
                                            </div>

                                            <div className="tab-content mb-3">
                                                <div className="card">
                                                    <div className="card-header text-uppercase bg-transparent">
                                                        <FormattedMessage
                                                            id="common.categories"
                                                            defaultMessage="Categories"
                                                        />
                                                        <div className="float-right">
                                                            <button
                                                                className="btn btn-outline-secondary btn-short"
                                                                onClick={this.handleAddCategoryItemToLine}
                                                                disabled={this.state.loading}
                                                            >
                                                                <i className="fas fa-plus mr-1" aria-hidden="true" />
                                                                {translate("common.add", "Add")}
                                                            </button>
                                                        </div>
                                                    </div>
                                                    {this.state.categories.length > 0 && (
                                                        <div className="card-body">
                                                            {this.state.categories.map(category => (
                                                                <div key={category.id}>
                                                                    <div>{category.name}</div>
                                                                    <dl className="list-group list-group-flush">
                                                                        {category.items.map(
                                                                            (item: ICategoryItemExt, index: number) => (
                                                                                <div
                                                                                    key={`${item.id || item.name}-${index}`}
                                                                                    className={classNames(
                                                                                        "list-group-item pr-0",
                                                                                        {
                                                                                            "text-muted": item.deleted,
                                                                                        }
                                                                                    )}
                                                                                >
                                                                                    <div className="d-flex justify-content-between align-items-center">
                                                                                        <div
                                                                                            className={classNames(
                                                                                                "flex-grow-1",
                                                                                                {
                                                                                                    "text-line-through":
                                                                                                        item.deleted,
                                                                                                }
                                                                                            )}
                                                                                        >
                                                                                            <div className="d-flex align-items-center">
                                                                                                <dt>
                                                                                                    <FormattedMessage
                                                                                                        id="common.name"
                                                                                                        defaultMessage="Name"
                                                                                                    />
                                                                                                    :
                                                                                                </dt>
                                                                                                <dd className="mb-0 ml-2">
                                                                                                    {item.name}
                                                                                                    {item.id !== null &&
                                                                                                        !this.state.original_category_item_ids.has(
                                                                                                            item.id
                                                                                                        ) && (
                                                                                                            <span className="badge badge-success ml-2">
                                                                                                                <FormattedMessage
                                                                                                                    id="common.new"
                                                                                                                    defaultMessage="New"
                                                                                                                />
                                                                                                            </span>
                                                                                                        )}
                                                                                                </dd>
                                                                                            </div>
                                                                                            <div className="text-muted mb-2 d-flex align-items-center">
                                                                                                <dt className="mr-2">
                                                                                                    <FormattedMessage
                                                                                                        id="common.description"
                                                                                                        defaultMessage="Description"
                                                                                                    />
                                                                                                    :
                                                                                                </dt>
                                                                                                <dd className="mb-0">
                                                                                                    {item.description ||
                                                                                                        "--"}
                                                                                                </dd>
                                                                                            </div>
                                                                                        </div>
                                                                                        <div className="ml-3">
                                                                                            {item.deleted ? (
                                                                                                <div className="d-flex align-items-center">
                                                                                                    <small className="text-muted mr-2">
                                                                                                        <i className="fas fa-trash-alt mr-1"></i>
                                                                                                        <FormattedMessage
                                                                                                            id="common.to_be_deleted"
                                                                                                            defaultMessage="To be deleted"
                                                                                                        />
                                                                                                    </small>
                                                                                                    <button
                                                                                                        className="btn btn-outline-warning btn-short px-1"
                                                                                                        onClick={() =>
                                                                                                            this.handleUndoRemoveCategoryItemFromLine(
                                                                                                                category,
                                                                                                                item
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        <i className="fas fa-undo"></i>
                                                                                                        <span className="sr-only">
                                                                                                            {translate(
                                                                                                                "common.undo",
                                                                                                                "Undo"
                                                                                                            )}
                                                                                                        </span>
                                                                                                    </button>
                                                                                                </div>
                                                                                            ) : (
                                                                                                <button
                                                                                                    className="btn btn-outline-danger btn-short px-1 ml-2"
                                                                                                    aria-label={translate(
                                                                                                        "common.remove",
                                                                                                        "Remove"
                                                                                                    )}
                                                                                                    title={translate(
                                                                                                        "common.remove",
                                                                                                        "Remove"
                                                                                                    )}
                                                                                                    onClick={() =>
                                                                                                        this.handleRemoveCategoryItemFromLine(
                                                                                                            category,
                                                                                                            item
                                                                                                        )
                                                                                                    }
                                                                                                >
                                                                                                    <i
                                                                                                        className="fas fa-trash-alt"
                                                                                                        aria-hidden="true"
                                                                                                    />
                                                                                                    <span className="sr-only">
                                                                                                        {translate(
                                                                                                            "common.remove",
                                                                                                            "Remove"
                                                                                                        )}
                                                                                                    </span>
                                                                                                </button>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </div>
                                                                            )
                                                                        )}
                                                                    </dl>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <LineEditTags tags={this.state.tags} onChangeCell={this.onChangeCell} />
                                            <div className="button-list button-list-entity-edit order-last ml-auto">
                                                <button className="btn btn-primary" onClick={this.handleSubmit}>
                                                    <FormattedMessage id="common.save" defaultMessage="Save" />
                                                </button>
                                                <button
                                                    className="btn btn-outline-secondary"
                                                    onClick={() => this.props.history.goBack()}
                                                >
                                                    <FormattedMessage id="common.back" defaultMessage="Back" />
                                                </button>
                                            </div>
                                            <LinesAddCategoryItemModal
                                                show={this.state.show_create_edit_category_modal}
                                                onHide={() => this.setState({ show_create_edit_category_modal: false })}
                                                onSave={this.handleSaveLineCategory}
                                                error={this.state.error}
                                                isLoading={this.state.loading}
                                                line_categories={this.state.categories.map(cat => ({
                                                    ...cat,
                                                    items: cat.items.map(item => ({
                                                        id: item.id,
                                                        name: item.name,
                                                        code: item.code,
                                                        description: item.description,
                                                    })),
                                                }))}
                                            />
                                        </React.Fragment>
                                    )}
                                </div>
                                <div className="space30"></div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }
}
